/**
 * ErrorBoundary Component for TimeSlots
 *
 * Catches React rendering errors in the TimeSlots component tree
 * and displays a user-friendly fallback UI with retry functionality.
 *
 * @requirements 8.1 - Display a fallback error message and recovery option on rendering error
 * @requirements 8.5 - Implement an ErrorBoundary component to catch and handle React rendering errors
 */

import React from 'react'
import styled from 'styled-components'
import { COLORS, FONTS } from '../utils/theme'

/**
 * Props for the ErrorFallback component
 */
interface ErrorFallbackProps {
  error: Error | null
  onRetry: () => void
}

/**
 * Props for the TimeSlotsErrorBoundary component
 */
// eslint-disable-next-line react/require-default-props
interface ErrorBoundaryProps {
  children: React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void // eslint-disable-line react/require-default-props
}

/**
 * State for the TimeSlotsErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

// --- Styled Components ---

const FallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 200px;
`

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`

const ErrorTitle = styled.h3`
  font-family: ${FONTS.bold};
  color: ${COLORS.text};
  font-size: 18px;
  margin: 0 0 8px 0;
`

const ErrorMessage = styled.p`
  font-family: ${FONTS.primary};
  color: ${COLORS.statusBlocked};
  font-size: 14px;
  margin: 0 0 24px 0;
  max-width: 400px;
`

const RetryButton = styled.button`
  font-family: ${FONTS.bold};
  font-size: 14px;
  padding: 10px 24px;
  border: none;
  border-radius: 20px;
  background-color: ${COLORS.primary};
  color: #ffffff;
  cursor: pointer;
  transition: opacity 0.15s ease-in-out;

  &:hover {
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid ${COLORS.focusRing};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * ErrorFallback Component
 *
 * Displays a user-friendly error message with a retry button.
 * Shown when the ErrorBoundary catches a rendering error.
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onRetry }) => {
  return (
    <FallbackContainer data-testid="error-fallback">
      <ErrorIcon>⚠️</ErrorIcon>
      <ErrorTitle>Something went wrong</ErrorTitle>
      <ErrorMessage>
        {error?.message || 'An unexpected error occurred while loading the schedule.'}
      </ErrorMessage>
      <RetryButton onClick={onRetry} data-testid="error-retry-button">
        Retry
      </RetryButton>
    </FallbackContainer>
  )
}

/**
 * TimeSlotsErrorBoundary
 *
 * React Error Boundary that catches rendering errors in the TimeSlots
 * component tree and displays the ErrorFallback UI.
 *
 * Usage:
 * ```tsx
 * <TimeSlotsErrorBoundary onError={(error, info) => logError(error, info)}>
 *   <TimeSlots ... />
 * </TimeSlotsErrorBoundary>
 * ```
 */
class TimeSlotsErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { onError } = this.props
    console.error('TimeSlots error:', error, errorInfo)
    onError?.(error, errorInfo)
  }

  render() {
    const { hasError, error } = this.state
    const { children } = this.props
    if (hasError) {
      return (
        <ErrorFallback
          error={error}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      )
    }
    return children
  }
}

export default TimeSlotsErrorBoundary
